// ═══════════════════════════════════════════════════════════════════════════
// DRE DO CONTRATO — mesma cascata, mesmos rótulos e mesmo visual da DRE geral
// (Dashboard.tsx), montada a partir do fato financeiro.dre_contrato_itens
// (decomposição da DRE geral por contrato — ver handlers/fechamentoCC.js).
//
// Lucas, 18/08/2026: "essa estrutura eu quero ver como vejo na DRE".
//
// A diferença que só existe aqui: cada linha pode ter uma parte DIRETA (custo
// que é deste contrato: nota com centro de custo, horas anotadas, dias de
// equipamento) e uma parte RATEADA (fatia da estrutura, proporcional à receita
// do mês). A linha mostra o total e, quando há parte direta, quanto é direto.
//
// ⚠️ Mês em andamento é sempre pior do que parece (despesa provisionada cedo,
//    receita só ao faturar — regra do CLAUDE.md). A janela vai do primeiro ao
//    último mês com lançamento; quando o mês corrente entra, o aviso aparece.
// ═══════════════════════════════════════════════════════════════════════════
import { useCallback, useEffect, useState } from 'react';
import { Alert, Button, Drawer, Empty, Space, Spin, Table, Tag, Tooltip, Typography } from 'antd';
import { ReloadOutlined, InfoCircleOutlined } from '@ant-design/icons';
import { API_URL } from '../config';
import { getToken } from '../utils/auth';
import { CORTE_ANALISE } from '../utils/avisosResultadoContrato';

const { Text } = Typography;

// ── Formatação idêntica à da DRE geral: o valor NUNCA leva sinal; a cor informa.
const fmt = (v: number) =>
  'R$ ' + Math.abs(v || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const pctDe = (v: number, base: number) => (base ? ((v / base) * 100).toFixed(1) + '%' : '-');
export const brl = (v: number) =>
  (v < 0 ? '-' : '') + 'R$ ' + Math.abs(v || 0).toLocaleString('pt-BR', { maximumFractionDigits: 0 });

const W_VAL = 140, W_PCT = 70, W_DIR = 130;

const ORIGEM_LABEL: Record<string, string> = {
  cc_lancamento: 'despesa lançada no CC',
  mao_obra_clockify: 'mão de obra (horas Clockify)',
  mao_obra_horas: 'mão de obra (horas anotadas no sistema)',
  mao_obra_padrao: 'mão de obra (alocação padrão — regra antiga)',
  mao_obra_pendente: 'mão de obra parada (não anotada ou sem contrato)',
  equipamento_checkin: 'equipamento (dias em campo)',
  combustivel_nota: 'combustível (nota do cartão Ticket Log)',
  comissao: 'comissão',
  rateio_receita: 'rateio por receita',
  imposto_recomputado: 'imposto sobre a base do contrato',
  imposto_rateado: 'imposto rateado (adicional/DARF)',
  overhead_nao_alocado: 'não alocado',
  provisao_encargos: 'encargos provisionados',
  receita_faturada: 'faturamento',
};

type LinhaDre = { total: number; direto: number; overhead: number; por_origem: Record<string, number> };
type Det = Record<string, LinhaDre>;
type Dre = {
  receitas: { recBruta: number; comissoes: number; recLiqComercial: number; detalhe: Det; comissoesDetalhe: Det };
  custos: { custosOp: number; detalhe: Det };
  lucroBruto: number;
  despesas: { despComercial: number; despAdmin: number; outrasDespOp: number; detalhe: { comercial: Det; admin: Det; outras: Det } };
  outrasReceitasOp: { total: number; detalhe: Det };
  estorno: { total: number; detalhe: Det };
  naoMapeados: { total: number; detalhe: Det };
  resultadoOp: number;
  financeiro: { despFin: number; recFin: number; detalhe: { despesas: Det; receitas: Det } };
  resultadoAntes: number;
  deducoes: { total: number; detalhe: Det };
  impostos: { total: number; detalhe: Det };
  lucroLiq: number;
  investimentos: { total: number; detalhe: Det };
  lucroFinal: number;
  _fechamento: { direto: number; overhead: number; custo_direto_por_origem: Record<string, number> };
};
type Resultado = {
  versao: string; contrato: any; de: string; ate: string; incluir_mes_corrente: boolean;
  acumulado: any; dre: Dre; meses: Record<string, any>;
  execucoes: { competencia: string; ok: boolean }[]; meses_sem_fechamento: string[]; aviso_mes_corrente: string | null;
};

async function api(path: string, opts?: RequestInit) {
  const token = await getToken();
  const r = await fetch(`${API_URL}${path}`, {
    ...opts, headers: { Authorization: token, 'Content-Type': 'application/json', ...(opts?.headers || {}) },
  });
  let d: any = await r.json().catch(() => ({}));
  if (d && typeof d === 'object' && 'body' in d && typeof d.body === 'string') { try { d = JSON.parse(d.body); } catch {} }
  if (!r.ok || d?.erro) throw new Error(d?.erro || `Erro ${r.status}`);
  return d;
}

export function DreContratoView({ contratoId, compacto }: { contratoId: string; compacto?: boolean }) {
  const [dados, setDados] = useState<Resultado | null>(null);
  const [loading, setLoading] = useState(false);
  const [erro, setErro] = useState<string | null>(null);
  const [abertos, setAbertos] = useState<Set<string>>(new Set(['rec', 'custos']));
  const [drill, setDrill] = useState<{ titulo: string; linha?: string } | null>(null);
  const [itens, setItens] = useState<any[] | null>(null);
  const [recalculando, setRecalculando] = useState(false);

  const carregar = useCallback(async () => {
    setLoading(true); setErro(null);
    try { setDados(await api(`/financeiro/contratos/${contratoId}/resultado`)); }
    catch (e: any) { setErro(e.message); }
    finally { setLoading(false); }
  }, [contratoId]);
  useEffect(() => { carregar(); }, [carregar]);

  function toggle(id: string) {
    setAbertos(s => { const n = new Set(s); if (n.has(id)) n.delete(id); else n.add(id); return n; });
  }

  async function abrirDrill(titulo: string, linha?: string) {
    setDrill({ titulo, linha }); setItens(null);
    try {
      const q = new URLSearchParams();
      if (linha) q.set('linha', linha);
      if (dados?.de) q.set('de', dados.de);
      if (dados?.ate) q.set('ate', dados.ate);
      const d = await api(`/financeiro/contratos/${contratoId}/resultado/itens?${q}`);
      setItens(d.itens || []);
    } catch { setItens([]); }
  }

  async function recalcular() {
    if (!dados) return;
    setRecalculando(true);
    try {
      // só os meses da análise (do corte em diante — 2025/01 desde 15/10/2026): o servidor recusa refazer os anteriores
      const meses = [...dados.meses_sem_fechamento, dados.ate].filter((v, i, a) => a.indexOf(v) === i && v >= CORTE_ANALISE).slice(-6);
      const falharam: string[] = [];
      for (const m of meses) {
        try { await api('/financeiro/fechamento-cc/recalcular', { method: 'POST', body: JSON.stringify({ competencia: m }) }); }
        catch { falharam.push(m); }
      }
      await carregar();
      if (falharam.length) setErro(`Recalcular: não foi possível refazer ${falharam.join(', ')} — tente de novo.`);
    } catch (e: any) { setErro('Recalcular: ' + e.message); }
    finally { setRecalculando(false); }
  }

  if (loading && !dados) return <div style={{ padding: 40, textAlign: 'center' }}><Spin /></div>;
  if (erro && !dados) return <Alert type="error" showIcon message={erro} />;
  if (!dados || !dados.dre) return <Empty />;
  const d = dados.dre;
  const rb = d.receitas.recBruta;

  // ── Componentes com o visual da DRE geral ────────────────────────────────
  function Linha({ label, l }: { label: string; l: LinhaDre }) {
    const v = l.total;
    return (
      <div
        style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '5px 24px',
          borderBottom: '0.5px solid #f0f0f0', fontSize: 13, cursor: 'pointer' }}
        onClick={() => abrirDrill(label, label)}
        title={`Ver de onde veio: ${label}`}
      >
        <span style={{ flex: 1, minWidth: 0, display: 'flex', alignItems: 'center', gap: 6 }}>
          {label}
          <span style={{ fontSize: 10, color: '#aaa' }}>▶</span>
        </span>
        <span style={{ width: W_DIR, textAlign: 'right', fontSize: 11, color: '#888' }}>
          {l.direto ? (
            <Tooltip title={`${fmt(l.direto)} é custo deste contrato (nota com centro de custo, horas apontadas ou dias de equipamento). O restante é a fatia da estrutura, rateada pela receita do mês.`}>
              <span>direto {fmt(l.direto)}</span>
            </Tooltip>
          ) : ''}
        </span>
        <span style={{ width: W_VAL, textAlign: 'right', color: v >= 0 ? '#0930D0' : '#E24B4A' }}>{fmt(v)}</span>
        <span style={{ width: W_PCT, textAlign: 'right', fontSize: 12, color: '#999' }}>{pctDe(v, rb)}</span>
      </div>
    );
  }

  function Grupo({ id, label, total, detalhe, tipo = 'neutro' }: {
    id: string; label: string; total: number; detalhe: Det; tipo?: 'receita' | 'despesa' | 'neutro';
  }) {
    const linhas = Object.entries(detalhe || {}).sort((a, b) => a[1].total - b[1].total);
    if (!total && linhas.length === 0) return null;
    const aberto = abertos.has(id);
    const azulCheio = id === 'rec';
    const direto = linhas.reduce((a, [, l]) => a + l.direto, 0);
    return (
      <div style={{ marginBottom: 2 }}>
        <div
          style={{ padding: '8px 12px', fontSize: 12, fontWeight: 500, letterSpacing: '0.04em', cursor: 'pointer',
            display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 2,
            background: azulCheio ? '#0930D0' : '#f0f0f0',
            color: azulCheio ? '#fff' : tipo === 'despesa' ? '#B91626' : '#0930D0' }}
          onClick={() => toggle(id)}
        >
          <span style={{ flex: 1, minWidth: 0 }}>{aberto ? '▾' : '▸'} {label}</span>
          <span style={{ width: W_DIR, textAlign: 'right', fontSize: 11, fontWeight: 400,
            color: azulCheio ? 'rgba(255,255,255,.85)' : '#666' }}>
            {direto ? `direto ${fmt(direto)}` : ''}
          </span>
          <span style={{ width: W_VAL, textAlign: 'right', fontSize: 13 }}>{fmt(total)}</span>
          <span style={{ width: W_PCT, textAlign: 'right', fontSize: 12,
            color: azulCheio ? 'rgba(255,255,255,.7)' : '#999' }}>{pctDe(total, rb)}</span>
        </div>
        {aberto && linhas.map(([nome, l]) => <Linha key={nome} label={nome} l={l} />)}
      </div>
    );
  }

  function Resultado({ label, valor }: { label: string; valor: number }) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', padding: '10px 12px',
        background: valor >= 0 ? '#EDF0FF' : '#FEF0EF', borderLeft: `3px solid ${valor >= 0 ? '#0930D0' : '#E24B4A'}`,
        margin: '4px 0', fontSize: 14, fontWeight: 500 }}>
        <span style={{ flex: 1, minWidth: 0 }}>{label}</span>
        <span style={{ width: W_DIR }} />
        <span style={{ width: W_VAL, textAlign: 'right', color: valor >= 0 ? '#0930D0' : '#E24B4A' }}>{fmt(valor)}</span>
        <span style={{ width: W_PCT, textAlign: 'right', fontSize: 12, color: '#999' }}>{pctDe(valor, rb)}</span>
      </div>
    );
  }

  const semDados = !rb && !d.custos.custosOp && !d._fechamento.overhead;

  return (
    <div>
      <Space wrap style={{ marginBottom: 12, justifyContent: 'space-between', width: '100%' }}>
        <Space wrap size={12}>
          <Tooltip title="Janela = do primeiro ao último mês com lançamento deste contrato">
            <Text type="secondary" style={{ fontSize: 12 }}>{dados.de} → {dados.ate} · motor {dados.versao}</Text>
          </Tooltip>
          <Tooltip title="Direto = custo do contrato: nota com centro de custo deste contrato + a remuneração da equipe pelas horas anotadas neste contrato (pago no mês ÷ horas anotadas no mês) + dias de equipamento em campo. Rateio = despesas da empresa (inclusive as horas em Administrativo, Comercial e outros centros internos) repartidas pela receita do mês.">
            <Text type="secondary" style={{ fontSize: 12 }}>
              <InfoCircleOutlined /> direto {brl(d._fechamento.direto)} · rateio {brl(d._fechamento.overhead)}
            </Text>
          </Tooltip>
        </Space>
        <Tooltip title="Recalcula a decomposição dos últimos meses a partir dos lançamentos atuais">
          <Button size="small" icon={<ReloadOutlined />} loading={recalculando} onClick={recalcular}>Recalcular</Button>
        </Tooltip>
      </Space>

      {dados.aviso_mes_corrente && <Alert type="warning" showIcon style={{ marginBottom: 12 }} message={dados.aviso_mes_corrente} />}
      {dados.meses_sem_fechamento.length > 0 && (
        <Alert type="info" showIcon style={{ marginBottom: 12 }}
          message={`Sem fechamento calculado para: ${dados.meses_sem_fechamento.join(', ')}`}
          description="O cron roda todo dia para os últimos meses; para meses mais antigos, use Recalcular." />
      )}
      {dados.execucoes.some(e => !e.ok) && (
        <Alert type="error" showIcon style={{ marginBottom: 12 }}
          message="Um ou mais meses não reconciliam com a DRE geral"
          description="A soma por contrato + empresa deveria bater centavo a centavo com a DRE." />
      )}

      {semDados ? <Empty description="Nada lançado neste contrato no período" /> : (
        <div style={{ maxWidth: compacto ? 820 : 1040 }}>
          {/* mesma ordem, mesmos rótulos da DRE geral (Dashboard.tsx) */}
          <Grupo id="rec" label="RECEITA BRUTA TOTAL" total={d.receitas.recBruta} detalhe={d.receitas.detalhe} tipo="receita" />
          <Grupo id="com" label="COMISSÕES DE VENDAS" total={d.receitas.comissoes} detalhe={d.receitas.comissoesDetalhe} tipo="despesa" />
          <Resultado label="RECEITA LÍQUIDA COMERCIAL" valor={d.receitas.recLiqComercial} />

          <Grupo id="custos" label="CUSTOS OPERACIONAIS" total={d.custos.custosOp} detalhe={d.custos.detalhe} tipo="despesa" />
          <Resultado label="LUCRO BRUTO (MARGEM DE CONTRIBUIÇÃO)" valor={d.lucroBruto} />

          <Grupo id="dc" label="DESPESAS COMERCIAIS E DE MARKETING" total={d.despesas.despComercial} detalhe={d.despesas.detalhe.comercial} tipo="despesa" />
          <Grupo id="da" label="DESPESAS ADMINISTRATIVAS" total={d.despesas.despAdmin} detalhe={d.despesas.detalhe.admin} tipo="despesa" />
          <Grupo id="od" label="OUTRAS DESPESAS OPERACIONAIS" total={d.despesas.outrasDespOp} detalhe={d.despesas.detalhe.outras} tipo="despesa" />
          <Grupo id="oro" label="OUTRAS RECEITAS OPERACIONAIS" total={d.outrasReceitasOp.total} detalhe={d.outrasReceitasOp.detalhe} tipo="receita" />
          <Grupo id="est" label="ESTORNO" total={d.estorno.total} detalhe={d.estorno.detalhe} tipo="receita" />
          <Grupo id="nm" label="CATEGORIAS NÃO MAPEADAS" total={d.naoMapeados.total} detalhe={d.naoMapeados.detalhe} tipo="despesa" />
          <Resultado label="RESULTADO OPERACIONAL" valor={d.resultadoOp} />

          <Grupo id="df" label="DESPESAS FINANCEIRAS" total={d.financeiro.despFin} detalhe={d.financeiro.detalhe.despesas} tipo="despesa" />
          <Grupo id="rf" label="RECEITAS FINANCEIRAS" total={d.financeiro.recFin} detalhe={d.financeiro.detalhe.receitas} tipo="receita" />
          <Resultado label="RESULTADO ANTES DOS TRIBUTOS" valor={d.resultadoAntes} />

          <Grupo id="ded" label="DEDUÇÕES" total={d.deducoes.total} detalhe={d.deducoes.detalhe} tipo="despesa" />
          <Grupo id="imp" label="IMPOSTOS" total={d.impostos.total} detalhe={d.impostos.detalhe} tipo="despesa" />
          <Resultado label="LUCRO LÍQUIDO" valor={d.lucroLiq} />

          <Grupo id="inv" label="INVESTIMENTOS EM IMOBILIZADO" total={d.investimentos.total} detalhe={d.investimentos.detalhe} tipo="despesa" />
          {d.investimentos.total !== 0 && <Resultado label="LUCRO FINAL (RESULTADO DO EXERCÍCIO)" valor={d.lucroFinal} />}

          {!compacto && <Detalhes contratoId={contratoId} dados={dados} />}
        </div>
      )}

      <Drawer open={!!drill} onClose={() => setDrill(null)} width="min(900px, 96vw)"
        title={drill ? `${drill.titulo} — de onde veio` : ''}>
        {itens == null ? <Spin /> : itens.length === 0 ? <Empty description="Sem itens nesta linha" /> : (
          <Table size="small" rowKey="id" pagination={{ pageSize: 50 }} dataSource={itens}
            columns={[
              { title: 'Mês', dataIndex: 'competencia', width: 80 },
              { title: 'Seção', dataIndex: 'secao', width: 100,
                render: (v: string) => <Tag color={v === 'custo_direto' ? 'blue' : undefined}>{v === 'custo_direto' ? 'direto' : v === 'overhead' ? 'rateio' : v}</Tag> },
              { title: 'Origem', dataIndex: 'origem', width: 170, render: (v: string) => ORIGEM_LABEL[v] || v },
              { title: 'De onde', key: 'de', ellipsis: true, render: (_: any, r: any) => {
                const rs = r.rastro || {}; const q = r.quantidade, t = r.taxa;
                if (r.origem === 'cc_lancamento') return `${rs.fornecedor || ''} · ${rs.descricao || ''}`;
                if (r.origem === 'rateio_receita') return `${Number.isFinite(q) ? q.toFixed(1) : '—'}% de ${brl(rs.pool)}`;
                if (String(r.origem).startsWith('mao_obra')) return `${r.profissional_nome || ''} · ${Number.isFinite(q) ? q.toFixed(1) : '—'} h × ${brl(t)}`;
                if (r.origem === 'combustivel_nota') return rs.descricao || `${rs.posto || ''} · ${rs.data || ''}`;
                if (r.origem === 'equipamento_checkin') return `${r.patrimonio_descricao || rs.bem || ''} · ${Number.isFinite(q) ? q : '—'} dia(s) × ${brl(t)}`;
                if (r.origem === 'receita_faturada') return rs.cliente || '';
                return rs.motivo || '';
              } },
              { title: 'Valor', dataIndex: 'valor', width: 130, align: 'right' as const, render: (v: number) => brl(v) },
            ]} />
        )}
      </Drawer>
    </div>
  );
}

// ── Blocos de apoio: mão de obra por pessoa, equipamento por bem, mês a mês ──
function Detalhes({ contratoId, dados }: { contratoId: string; dados: Resultado }) {
  const [aba, setAba] = useState<'mao_obra' | 'equip' | 'meses' | null>(null);
  const [maoObra, setMaoObra] = useState<any[] | null>(null);
  const [equip, setEquip] = useState<any[] | null>(null);
  // 🚨 Falha ao carregar NÃO é lista vazia: "Nenhuma hora alocada a este
  // contrato" e "não consegui ler" não podem ser a mesma tela — é a tela do R$/h.
  const [falhou, setFalhou] = useState<{ mao_obra?: boolean; equip?: boolean }>({});

  async function carregar(qual: 'mao_obra' | 'equip') {
    setFalhou(f => ({ ...f, [qual]: false }));
    const q = new URLSearchParams({ secao: 'custo_direto' });
    if (dados.de) q.set('de', dados.de);
    if (dados.ate) q.set('ate', dados.ate);
    try {
      const d = await api(`/financeiro/contratos/${contratoId}/resultado/itens?${q}`);
      const its = d.itens || [];
      if (qual === 'mao_obra') {
        const porPessoa: Record<string, any> = {};
        for (const i of its.filter((x: any) => String(x.origem).startsWith('mao_obra'))) {
          const k = i.profissional_id || i.profissional_nome || '?';
          const g = porPessoa[k] || (porPessoa[k] = { key: k, nome: i.profissional_nome || '—', h_anotador: 0, h_clockify: 0, h_padrao: 0, valor: 0, meses: new Set<string>() });
          const h = Number.isFinite(i.quantidade) ? i.quantidade : 0;
          // anotadas no sistema (desde 23/09/2026) e Clockify (até então); a
          // alocação padrão saiu em 06/10/2026 e só aparece em mês antigo
          if (i.origem === 'mao_obra_padrao') g.h_padrao += h;
          else if (i.origem === 'mao_obra_horas') g.h_anotador += h;
          else g.h_clockify += h;
          g.valor += i.valor || 0; g.meses.add(i.competencia);
        }
        setMaoObra(Object.values(porPessoa).map((g: any) => {
          const hTotal = g.h_anotador + g.h_clockify + g.h_padrao;
          return { ...g, meses: g.meses.size, rs_hora: hTotal > 0 ? Math.abs(g.valor) / hTotal : 0 };
        })
          .sort((a: any, b: any) => a.valor - b.valor));
      } else {
        const porBem: Record<string, any> = {};
        for (const i of its.filter((x: any) => x.origem === 'equipamento_checkin')) {
          const k = i.patrimonio_id || '?';
          const g = porBem[k] || (porBem[k] = { key: k, nome: i.patrimonio_descricao || i.rastro?.bem || '—', codigo: i.rastro?.codigo || '', diasPorMes: {} as Record<string, number>, valor: 0, meses: new Set<string>() });
          const qt = Number.isFinite(i.quantidade) ? i.quantidade : 0;
          g.diasPorMes[i.competencia] = Math.max(g.diasPorMes[i.competencia] || 0, qt);
          g.valor += i.valor || 0; g.meses.add(i.competencia);
        }
        setEquip(Object.values(porBem).map((g: any) => {
          const dias = Object.values(g.diasPorMes as Record<string, number>).reduce((a, b) => a + b, 0);
          return { ...g, dias, meses: g.meses.size, rs_dia: dias > 0 ? Math.abs(g.valor) / dias : 0 };
        }).sort((a: any, b: any) => a.valor - b.valor));
      }
    } catch { setFalhou(f => ({ ...f, [qual]: true })); }
  }
  const avisoDeFalha = (qual: 'mao_obra' | 'equip') => (
    <Alert style={{ marginTop: 12 }} type="error" showIcon
      message={qual === 'mao_obra' ? 'Não consegui carregar a mão de obra deste contrato.' : 'Não consegui carregar os equipamentos deste contrato.'}
      action={<Button size="small" onClick={() => carregar(qual)}>Tentar de novo</Button>} />
  );

  function abrir(qual: 'mao_obra' | 'equip' | 'meses') {
    setAba(a => (a === qual ? null : qual));
    if (qual === 'mao_obra' && maoObra == null) carregar('mao_obra');
    if (qual === 'equip' && equip == null) carregar('equip');
  }

  return (
    <div style={{ marginTop: 16 }}>
      <Space wrap>
        <Button size="small" type={aba === 'mao_obra' ? 'primary' : 'default'} onClick={() => abrir('mao_obra')}>Mão de obra por pessoa</Button>
        <Button size="small" type={aba === 'equip' ? 'primary' : 'default'} onClick={() => abrir('equip')}>Equipamento por bem</Button>
        <Button size="small" type={aba === 'meses' ? 'primary' : 'default'} onClick={() => abrir('meses')}>Mês a mês</Button>
      </Space>

      {aba === 'mao_obra' && (falhou.mao_obra ? avisoDeFalha('mao_obra') : maoObra == null ? <Spin size="small" style={{ marginTop: 12 }} /> : maoObra.length === 0
        ? <Text type="secondary" style={{ display: 'block', marginTop: 12 }}>Nenhuma hora alocada a este contrato no período.</Text>
        : <Table style={{ marginTop: 12 }} size="small" pagination={false} rowKey="key" dataSource={maoObra}
            scroll={{ x: 760 }}
            columns={[
              { title: 'Pessoa', dataIndex: 'nome', ellipsis: true },
              { title: 'h no sistema', dataIndex: 'h_anotador', width: 110, align: 'right' as const,
                render: (v: number) => v > 0 ? <Tooltip title="Horas anotadas no sistema (Operacional › Horas)">{v.toFixed(1)}</Tooltip> : '—' },
              { title: 'h Clockify', dataIndex: 'h_clockify', width: 100, align: 'right' as const, render: (v: number) => v > 0 ? v.toFixed(1) : '—' },
              // só em mês calculado pela regra antiga (antes de 06/10/2026)
              ...(maoObra.some((g: any) => g.h_padrao > 0) ? [{ title: 'h padrão (regra antiga)', dataIndex: 'h_padrao', width: 120, align: 'right' as const,
                render: (v: number) => v > 0 ? <Tooltip title="Mês calculado antes de 06/10/2026: horas não anotadas preenchidas pela alocação padrão. Recalcule o mês para a regra das horas anotadas.">{v.toFixed(1)}</Tooltip> : '—' }] : []),
              { title: <Tooltip title="Média do período: o que foi pago à pessoa em cada mês ÷ as horas que ela anotou no mês">R$/h</Tooltip>,
                dataIndex: 'rs_hora', width: 100, align: 'right' as const, render: (v: number) => v ? brl(v) : '—' },
              { title: 'Meses', dataIndex: 'meses', width: 70, align: 'right' as const },
              { title: <Tooltip title="Pago no mês ÷ horas anotadas no mês × horas neste contrato, somado nos meses">Custo</Tooltip>,
                dataIndex: 'valor', width: 130, align: 'right' as const, render: (v: number) => <b>{brl(v)}</b> },
            ]} />)}

      {aba === 'equip' && (falhou.equip ? avisoDeFalha('equip') : equip == null ? <Spin size="small" style={{ marginTop: 12 }} /> : equip.length === 0
        ? <Text type="secondary" style={{ display: 'block', marginTop: 12 }}>Nenhum kit fez check-in para este contrato no período.</Text>
        : <Table style={{ marginTop: 12 }} size="small" pagination={false} rowKey="key" dataSource={equip}
            columns={[
              { title: 'Bem', key: 'b', ellipsis: true, render: (_: any, r: any) => <>{r.codigo ? <Tag>{r.codigo}</Tag> : null}{r.nome}</> },
              { title: 'Dias úteis', dataIndex: 'dias', width: 100, align: 'right' as const, render: (v: number) => v.toFixed(1) },
              { title: 'R$/dia', dataIndex: 'rs_dia', width: 100, align: 'right' as const, render: (v: number) => v ? brl(v) : '—' },
              { title: 'Meses', dataIndex: 'meses', width: 70, align: 'right' as const },
              { title: 'Custo', dataIndex: 'valor', width: 130, align: 'right' as const, render: (v: number) => <b>{brl(v)}</b> },
            ]} />)}

      {aba === 'meses' && (
        <Table style={{ marginTop: 12 }} size="small" pagination={false} rowKey="mes"
          dataSource={Object.entries(dados.meses).sort().map(([mes, m]: any) => ({ mes, ...m }))}
          columns={[
            { title: 'Mês', dataIndex: 'mes', width: 90 },
            { title: 'Receita', dataIndex: 'receita', align: 'right' as const, render: brl },
            { title: 'Direto', dataIndex: 'custo_direto', align: 'right' as const, render: brl },
            { title: 'Rateio', dataIndex: 'overhead', align: 'right' as const, render: brl },
            { title: 'Impostos', dataIndex: 'impostos', align: 'right' as const, render: (v: number, r: any) => brl(v + r.deducoes) },
            { title: 'Lucro', dataIndex: 'lucro', align: 'right' as const, render: (v: number) => <b>{brl(v)}</b> },
          ]} />
      )}
    </div>
  );
}
